import React, { useEffect, useState, useCallback } from 'react';
import { ShieldAlert, Clock, LogOut, Tag, Loader2 } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';

function fmtRemaining(ms) {
  if (ms == null) return '';
  if (ms <= 0) return 'expired';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
}

/**
 * ActiveSupportBanner
 * --------------------
 * Sticky red banner shown to an employer/agent while a platform admin
 * has an active support session inside their account. Includes a live
 * countdown to session expiry and a Revoke button that ends the
 * session immediately (backend audits the revoke).
 */
export default function ActiveSupportBanner() {
  const [session, setSession] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await apiCall('/api/support-mode/active-session');
      setSession(r.viewer === 'org' ? r.session : null);
    } catch {
      setSession(null);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!session) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [session]);

  if (!session) return null;

  const remainingMs = session.expires_at
    ? new Date(session.expires_at).getTime() - now
    : null;
  const soonExpiring = remainingMs != null && remainingMs < 5 * 60 * 1000;
  const scopes = session.scopes || ['all'];
  const scopesLabel = scopes.includes('all') ? 'Full access' : scopes.join(' · ');

  const revoke = async () => {
    if (!window.confirm('End this support session immediately? The admin will lose access right away.')) return;
    setRevoking(true);
    try {
      await apiCall(`/api/employer/support-mode/${session._id}/revoke`, { method: 'POST' });
      toast.success('Support session ended.');
      setSession(null);
    } catch (e) {
      toast.error(e?.message || 'Could not revoke');
    }
    setRevoking(false);
  };

  return (
    <div
      className="sticky top-0 z-40 bg-gradient-to-r from-rose-600 to-orange-600 text-white shadow-md"
      data-testid="active-support-banner"
    >
      <div className="container mx-auto px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <ShieldAlert className="w-4 h-4 flex-shrink-0" />
          <div className="text-sm leading-tight min-w-0">
            <p className="font-bold">
              A platform administrator is inside your account
            </p>
            <p className="text-xs text-white/90 truncate">
              <span className="font-semibold">{session.admin_name || session.admin_email}</span>
              <span className="text-white/70"> · {session.mode === 'forced' ? 'Forced entry' : 'Consent granted'}</span>
              {session.reason ? <span className="text-white/70"> · {session.reason}</span> : null}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {remainingMs != null && (
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold ${
                soonExpiring ? 'bg-yellow-300 text-yellow-900 animate-pulse' : 'bg-white/15 ring-1 ring-white/20'
              }`}
              data-testid="active-support-countdown"
            >
              <Clock className="w-3 h-3" /> {fmtRemaining(remainingMs)}
            </span>
          )}
          <span
            className="hidden md:inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-white/15 ring-1 ring-white/20"
            data-testid="active-support-scopes"
          >
            <Tag className="w-3 h-3" /> {scopesLabel}
          </span>
          <button
            onClick={revoke}
            disabled={revoking}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-rose-700 hover:bg-rose-50 rounded-lg text-xs font-bold disabled:opacity-60"
            data-testid="active-support-revoke"
          >
            {revoking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />} Revoke
          </button>
        </div>
      </div>
    </div>
  );
}
